/**
 * Calendar Event Mapper
 * Utilities to convert between frontend CalendarEvent and API CalendarEvent types
 */

import { CalendarEvent as FrontendEvent } from '@/types/calendar';
import {
  CalendarEventListItem as APIEventListItem,
  CalendarEvent as APIEvent,
  CalendarEventCreateData,
  EventType as APIEventType,
} from '@/services/api/calendarService';

// ============================================================================
// TYPE MAPPINGS
// ============================================================================

/**
 * Maps frontend categoryId to API event_type
 */
const categoryToEventType: Record<string, APIEventType> = {
  'meeting-presencial': 'meeting_in_person',
  'meeting-virtual': 'video_call',
  'call': 'phone_call',
  'visit-project': 'site_visit',
  'launch-event': 'open_house',
  'presentation': 'quote_review',
  'training': 'training',
  'follow-up': 'follow_up',
  'special': 'other',
};

/**
 * Maps API event_type to frontend categoryId
 */
const eventTypeToCategoryId: Record<APIEventType, string> = {
  'phone_call': 'call',
  'video_call': 'meeting-virtual',
  'meeting_in_person': 'meeting-presencial',
  'site_visit': 'visit-project',
  'unit_showing': 'visit-project',
  'open_house': 'launch-event',
  'quote_review': 'presentation',
  'contract_signing': 'special',
  'document_delivery': 'special',
  'follow_up': 'follow-up',
  'reminder': 'follow-up',
  'team_meeting': 'meeting-presencial',
  'training': 'training',
  'other': 'special',
};

/**
 * Maps frontend type to API event_type
 */
const frontendTypeToApiType: Record<string, APIEventType> = {
  'meeting': 'meeting_in_person',
  'call': 'phone_call',
  'visit': 'site_visit',
  'follow-up': 'follow_up',
  'launch': 'open_house',
  'presentation': 'quote_review',
  'training': 'training',
  'other': 'other',
};

/**
 * Maps API event_type to frontend type
 */
const apiTypeToFrontendType: Record<APIEventType, FrontendEvent['type']> = {
  'phone_call': 'call',
  'video_call': 'meeting',
  'meeting_in_person': 'meeting',
  'site_visit': 'visit',
  'unit_showing': 'visit',
  'open_house': 'launch',
  'quote_review': 'presentation',
  'contract_signing': 'other',
  'document_delivery': 'other',
  'follow_up': 'follow-up',
  'reminder': 'follow-up',
  'team_meeting': 'meeting',
  'training': 'training',
  'other': 'other',
};

/**
 * Maps API status to frontend status
 */
const apiStatusToFrontend: Record<string, FrontendEvent['status']> = {
  'scheduled': 'scheduled',
  'confirmed': 'scheduled',
  'in_progress': 'scheduled',
  'completed': 'completed',
  'cancelled': 'cancelled',
  'no_show': 'cancelled',
  'rescheduled': 'scheduled',
};

/**
 * Maps API source to frontend source
 */
const apiSourceToFrontend: Record<string, FrontendEvent['source']> = {
  'manual': 'manual',
  'google': 'google',
  'outlook': 'outlook',
  'system': 'system',
  'automation': 'system',
};

// ============================================================================
// MAPPER FUNCTIONS
// ============================================================================

/**
 * Convert API event list item to frontend CalendarEvent
 */
export function mapAPIEventToFrontend(apiEvent: APIEventListItem | APIEvent): FrontendEvent {
  return {
    id: apiEvent.id,
    title: apiEvent.title,
    description: 'notes' in apiEvent ? apiEvent.notes : undefined,
    startTime: new Date(apiEvent.start_time),
    endTime: new Date(apiEvent.end_time),
    categoryId: eventTypeToCategoryId[apiEvent.event_type] || 'special',
    type: apiTypeToFrontendType[apiEvent.event_type] || 'other',
    leadId: apiEvent.lead || undefined,
    leadName: 'lead_name' in apiEvent ? apiEvent.lead_name || undefined : undefined,
    location: apiEvent.location || undefined,
    meetingLink: apiEvent.meeting_link || undefined,
    attendees: [],
    status: apiStatusToFrontend[apiEvent.status] || 'scheduled',
    source: apiSourceToFrontend[apiEvent.source] || 'manual',
    isVirtual: apiEvent.event_type === 'video_call',
    timezone: 'timezone' in apiEvent ? apiEvent.timezone || undefined : undefined,
  };
}

/**
 * Convert an array of API events to frontend events
 */
export function mapAPIEventsToFrontend(apiEvents: (APIEventListItem | APIEvent)[]): FrontendEvent[] {
  return apiEvents.map(mapAPIEventToFrontend);
}

/**
 * Convert frontend event to API create data
 *
 * @param frontendEvent - The frontend event data
 * @param options - Additional options for creating the event
 *   - clientId: Optional client ID to associate with the event
 *   - projectId: Optional project ID to associate with the event
 *   - companyId: Optional company ID (for platform users to assign to tenant)
 *   - userId: Optional user ID (for platform users to assign event to specific advisor)
 */
export function mapFrontendEventToAPICreate(
  frontendEvent: Partial<FrontendEvent>,
  options?: { clientId?: string; projectId?: string; companyId?: string; userId?: string }
): CalendarEventCreateData {
  const eventType = frontendEvent.categoryId
    ? categoryToEventType[frontendEvent.categoryId]
    : frontendTypeToApiType[frontendEvent.type || 'meeting'] || 'meeting_in_person';

  return {
    title: frontendEvent.title || '',
    description: frontendEvent.description,
    start_time: frontendEvent.startTime?.toISOString() || new Date().toISOString(),
    end_time: frontendEvent.endTime?.toISOString() || new Date().toISOString(),
    event_type: eventType,
    location: frontendEvent.location,
    meeting_link: frontendEvent.meetingLink,
    lead: frontendEvent.leadId,
    client: options?.clientId,
    project: options?.projectId,
    company: options?.companyId,
    user: options?.userId,
    notes: frontendEvent.description,
    // Default values
    all_day: false,
    timezone: 'America/Bogota',
    status: 'scheduled',
    priority: 'medium',
  };
}

/**
 * Convert frontend event to API update data
 */
export function mapFrontendEventToAPIUpdate(frontendEvent: Partial<FrontendEvent>): Partial<CalendarEventCreateData> {
  const data: Partial<CalendarEventCreateData> = {};

  if (frontendEvent.title !== undefined) {
    data.title = frontendEvent.title;
  }
  if (frontendEvent.description !== undefined) {
    data.description = frontendEvent.description;
    data.notes = frontendEvent.description;
  }
  if (frontendEvent.startTime !== undefined) {
    data.start_time = frontendEvent.startTime.toISOString();
  }
  if (frontendEvent.endTime !== undefined) {
    data.end_time = frontendEvent.endTime.toISOString();
  }
  if (frontendEvent.categoryId !== undefined) {
    data.event_type = categoryToEventType[frontendEvent.categoryId] || 'meeting_in_person';
  }
  if (frontendEvent.location !== undefined) {
    data.location = frontendEvent.location;
  }
  if (frontendEvent.meetingLink !== undefined) {
    data.meeting_link = frontendEvent.meetingLink;
  }
  if (frontendEvent.leadId !== undefined) {
    data.lead = frontendEvent.leadId;
  }

  return data;
}

/**
 * Get color for an event based on its category/type
 */
export function getEventColor(eventType: APIEventType): string {
  const colorMap: Record<APIEventType, string> = {
    'phone_call': '#9333EA', // purple
    'video_call': '#16A34A', // green
    'meeting_in_person': '#2563EB', // blue
    'site_visit': '#EA580C', // orange
    'unit_showing': '#EA580C', // orange
    'open_house': '#EC4899', // pink
    'quote_review': '#6366F1', // indigo
    'contract_signing': '#F43F5E', // rose
    'document_delivery': '#F43F5E', // rose
    'follow_up': '#CA8A04', // yellow
    'reminder': '#CA8A04', // yellow
    'team_meeting': '#2563EB', // blue
    'training': '#0D9488', // teal
    'other': '#F43F5E', // rose
  };

  return colorMap[eventType] || '#3B82F6';
}
